import React, { useEffect, useState } from 'react';
import { Container, Card, Button, Form, Alert, Badge, Row, Col, ProgressBar, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../utils/ThemeContext';
import { callCreateCheckoutSession, callCreatePortalSession } from '../utils/firebase';
import { trackEvent, trackPageView, trackFunnelEvent } from '../utils/analytics';

// Tier display config
const TIER_CONFIG = {
  free: {
    label: 'Free',
    color: 'secondary',
    limits: { messagesPerMonth: 50, imageGenerationsLabel: '5 / year', messagesPerDay: 500 },
  },
  homeowner: {
    label: 'Homeowner',
    color: 'primary',
    limits: { messagesPerMonth: 500, imageGenerationsLabel: '50 / month', messagesPerDay: 500 },
  },
  professional: {
    label: 'Professional',
    color: 'warning',
    limits: { messagesPerMonth: 5000, imageGenerationsLabel: '500 / month', messagesPerDay: 500 },
  },
};

// Stripe price IDs — set via environment variables
const HOMEOWNER_PRICE_ID = process.env.REACT_APP_STRIPE_HOMEOWNER_PRICE_ID || '';
const PROFESSIONAL_PRICE_ID = process.env.REACT_APP_STRIPE_PROFESSIONAL_PRICE_ID || '';

function ProfilePage({ user, subscription, usage, onSignOut, onDeleteAllData, onBack }) {
  const navigate = useNavigate();
  const { preference, setThemePreference } = useTheme();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deleteStep, setDeleteStep] = useState(1);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(null); // 'homeowner' | 'professional' | null
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    trackPageView('/profile');
  }, []);

  // Log the subscription prop (sourced from the Firebase realtime listener
  // in subscribeToSubscription) so the data ProfilePage actually renders can
  // be diffed against the sync cloud function's response. Distinct prefix
  // from [syncSubscription] so the two streams are easy to tell apart.
  useEffect(() => {
    console.log('[ProfilePage] Firebase subscription data (from RTDB listener):', JSON.stringify(subscription));
  }, [subscription]);

  const tier = subscription?.tier || 'free';
  const tierConfig = TIER_CONFIG[tier] || TIER_CONFIG.free;
  // Use backend-provided limits (source of truth) when available, fall back to frontend config
  const limits = subscription?.tierLimits || tierConfig.limits;
  const status = subscription?.status;
  const cancelAtPeriodEnd = subscription?.cancelAtPeriodEnd;
  const cancelAt = subscription?.cancelAt;
  const currentPeriodEnd = subscription?.currentPeriodEnd;
  const requiresAction = subscription?.requiresAction;
  const hostedInvoiceUrl = subscription?.hostedInvoiceUrl;
  // A cancellation is pending if either Stripe's cancel_at_period_end flag is
  // set OR a future cancel_at timestamp is scheduled. cancel_at is the
  // authoritative date; fall back to currentPeriodEnd for the legacy case.
  const cancellationPending = !!(cancelAtPeriodEnd || cancelAt);
  const cancellationDate = cancelAt || currentPeriodEnd;

  const formatDate = (unixSeconds) => {
    if (!unixSeconds) return null;
    return new Date(unixSeconds * 1000).toLocaleDateString(undefined, {
      year: 'numeric', month: 'long', day: 'numeric',
    });
  };

  // One-line subscription status summary shown below the tier badge.
  const statusSummary = (() => {
    if (tier === 'free') return null;
    if (requiresAction) return { text: 'Action required', icon: 'bi-shield-exclamation', color: 'var(--bs-danger)' };
    if (status === 'past_due') return { text: 'Payment past due', icon: 'bi-exclamation-triangle', color: 'var(--bs-warning)' };
    if (cancellationPending && cancellationDate) return { text: `Cancels on ${formatDate(cancellationDate)}`, icon: 'bi-clock-history', color: 'var(--bs-info)' };
    if (cancellationPending) return { text: 'Cancellation pending', icon: 'bi-clock-history', color: 'var(--bs-info)' };
    if (status === 'active' && currentPeriodEnd) return { text: `Renews on ${formatDate(currentPeriodEnd)}`, icon: 'bi-check-circle', color: 'var(--bs-success)' };
    if (status === 'active') return { text: 'Active', icon: 'bi-check-circle', color: 'var(--bs-success)' };
    if (status === 'canceled') return { text: 'Canceled', icon: 'bi-x-circle', color: 'var(--bs-secondary)' };
    return null;
  })();

  // Usage data
  const currentPeriod = (() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  })();
  const msgUsage = usage?.messages || {};
  const messagesUsed = (msgUsage.period === currentPeriod) ? (msgUsage.count || 0) : 0;

  const imgUsage = usage?.imageGenerations || {};
  const imagesUsed = (imgUsage.period === currentPeriod) ? (imgUsage.count || 0) : 0;

  const handleDeleteClick = () => {
    setDeleteStep(1);
    setDeleteConfirmation('');
    setError(null);
    setShowDeleteModal(true);
  };

  const handleFirstConfirm = () => {
    setDeleteStep(2);
  };

  const handleFinalDelete = async () => {
    if (deleteConfirmation.toLowerCase() !== 'delete') {
      setError('Please type "delete" to confirm');
      return;
    }

    setIsDeleting(true);
    setError(null);

    try {
      await onDeleteAllData();
      setShowDeleteModal(false);
      onSignOut();
    } catch (err) {
      setError(err.message || 'Failed to delete data. Please try again.');
      setIsDeleting(false);
    }
  };

  const handleCloseModal = () => {
    if (!isDeleting) {
      setShowDeleteModal(false);
      setDeleteStep(1);
      setDeleteConfirmation('');
      setError(null);
    }
  };

  const handleUpgrade = async (tierName) => {
    const priceId = tierName === 'homeowner' ? HOMEOWNER_PRICE_ID : PROFESSIONAL_PRICE_ID;
    if (!priceId) {
      setError('Subscription setup is not yet complete. Please try again later.');
      return;
    }
    trackEvent('subscription_upgrade_clicked', { tier: tierName });
    // Funnel step 9: user committed to the upgrade flow (Stripe redirect
    // about to happen). Mirrors the diagnostic event so step 9 has a stable
    // funnel-only counterpart.
    trackFunnelEvent('subscription_form_started', { tier: tierName });
    setCheckoutLoading(tierName);
    try {
      const result = await callCreateCheckoutSession(priceId);
      if (result.url) {
        window.location.href = result.url;
      }
    } catch (err) {
      setError(err.message || 'Failed to start checkout. Please try again.');
      setCheckoutLoading(null);
    }
  };

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const result = await callCreatePortalSession();
      if (result.url) {
        window.location.href = result.url;
      }
    } catch (err) {
      setError(err.message || 'Failed to open subscription portal. Please try again.');
      setPortalLoading(false);
    }
  };

  return (
    <div className="profile-page h-100 overflow-auto">
      <Container className="py-4" style={{ maxWidth: '600px' }}>
        {/* Back button for mobile */}
        <Button
          variant="link"
          className="text-decoration-none mb-3 p-0"
          onClick={onBack}
        >
          <i className="bi bi-arrow-left me-2"></i>
          Back
        </Button>

        {/* Profile Header */}
        <Card className="mb-4 border-0 shadow-sm">
          <Card.Body className="text-center py-4">
            <p className="text-muted mb-0">{user.email}</p>
          </Card.Body>
        </Card>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-4">
            {error}
          </Alert>
        )}

        {/* Subscription Tier */}
        <Card className="mb-4 border-0 shadow-sm">
          <Card.Header className="bg-white border-bottom">
            <div className="d-flex align-items-center justify-content-between">
              <h6 className="mb-0">
                <i className="bi bi-star me-2"></i>
                Subscription
              </h6>
              <Badge bg={tierConfig.color} className="py-2 px-3">
                {tierConfig.label}
              </Badge>
            </div>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col xs={12}>
                {/* Subscription status summary */}
                {statusSummary && (
                  <div className="mb-3 small d-flex align-items-center" style={{ color: statusSummary.color }}>
                    <i className={`bi ${statusSummary.icon} me-2`}></i>
                    <span>{statusSummary.text}</span>
                  </div>
                )}

                {/* Status alerts */}
                {requiresAction && (
                  <Alert variant="danger" className="mb-3">
                    <i className="bi bi-shield-exclamation me-2"></i>
                    <strong>Payment authentication required.</strong> Your bank needs to verify your last payment before your subscription can renew.
                    {hostedInvoiceUrl && (
                      <div className="mt-2">
                        <Button
                          size="sm"
                          variant="danger"
                          href={hostedInvoiceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Complete payment
                        </Button>
                      </div>
                    )}
                  </Alert>
                )}
                {status === 'past_due' && !requiresAction && (
                  <Alert variant="warning" className="mb-3">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    Your payment is past due. Please update your payment method to avoid service interruption.
                    <div className="mt-2">
                      <Button
                        size="sm"
                        variant="warning"
                        onClick={handleManageSubscription}
                        disabled={portalLoading}
                      >
                        {portalLoading ? (
                          <><span className="spinner-border spinner-border-sm me-2" />Opening...</>
                        ) : (
                          'Update payment method'
                        )}
                      </Button>
                    </div>
                  </Alert>
                )}
                {cancellationPending && (
                  <Alert variant="info" className="mb-3">
                    <i className="bi bi-info-circle me-2"></i>
                    {cancellationDate
                      ? <>Your subscription will end on <strong>{formatDate(cancellationDate)}</strong>. You'll keep {tierConfig.label} access until then.</>
                      : 'Your subscription will be canceled at the end of the current billing period.'
                    }
                  </Alert>
                )}

                {/* Usage stats */}
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <small className="text-muted">AI Messages this month</small>
                    <small className="text-muted">
                      {messagesUsed} / {limits.messagesPerMonth}
                    </small>
                  </div>
                  <ProgressBar
                    now={(messagesUsed / limits.messagesPerMonth) * 100}
                    variant={messagesUsed >= limits.messagesPerMonth ? 'danger' : 'primary'}
                    style={{ height: '6px' }}
                  />
                </div>

                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <small className="text-muted">Design generations</small>
                    <small className="text-muted">
                      {imagesUsed} used ({limits.imageGenerationsLabel})
                    </small>
                  </div>
                </div>

                {/* Upgrade / Manage buttons */}
                {tier === 'free' && (
                  <div className="mt-4 pt-3 border-top">
                    <p className="text-muted small mb-3">Upgrade for more AI messages and design generations.</p>
                    <div className="d-grid gap-2">
                      <Button
                        variant="primary"
                        onClick={() => handleUpgrade('homeowner')}
                        disabled={!!checkoutLoading}
                      >
                        {checkoutLoading === 'homeowner' ? (
                          <><span className="spinner-border spinner-border-sm me-2" />Processing...</>
                        ) : (
                          <>Upgrade to Homeowner — $29.99/mo</>
                        )}
                      </Button>
                      <Button
                        variant="outline-warning"
                        onClick={() => handleUpgrade('professional')}
                        disabled={!!checkoutLoading}
                      >
                        {checkoutLoading === 'professional' ? (
                          <><span className="spinner-border spinner-border-sm me-2" />Processing...</>
                        ) : (
                          <>Upgrade to Professional — $199/mo</>
                        )}
                      </Button>
                    </div>
                  </div>
                )}

                {tier === 'homeowner' && (
                  <div className="mt-4 pt-3 border-top">
                    <div className="d-grid gap-2">
                      <Button
                        variant="outline-warning"
                        onClick={() => handleUpgrade('professional')}
                        disabled={!!checkoutLoading}
                      >
                        {checkoutLoading === 'professional' ? (
                          <><span className="spinner-border spinner-border-sm me-2" />Processing...</>
                        ) : (
                          <>Upgrade to Professional — $199/mo</>
                        )}
                      </Button>
                      <Button
                        variant="outline-secondary"
                        onClick={handleManageSubscription}
                        disabled={portalLoading}
                      >
                        {portalLoading ? (
                          <><span className="spinner-border spinner-border-sm me-2" />Opening...</>
                        ) : (
                          <><i className="bi bi-gear me-2"></i>Manage Subscription</>
                        )}
                      </Button>
                    </div>
                  </div>
                )}

                {tier === 'professional' && (
                  <div className="mt-4 pt-3 border-top">
                    <Button
                      variant="outline-secondary"
                      className="w-100"
                      onClick={handleManageSubscription}
                      disabled={portalLoading}
                    >
                      {portalLoading ? (
                        <><span className="spinner-border spinner-border-sm me-2" />Opening...</>
                      ) : (
                        <><i className="bi bi-gear me-2"></i>Manage Subscription</>
                      )}
                    </Button>
                  </div>
                )}
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Appearance */}
        <Card className="mb-4 border-0 shadow-sm">
          <Card.Header className="bg-white border-bottom">
            <h6 className="mb-0">
              <i className="bi bi-palette me-2"></i>
              Appearance
            </h6>
          </Card.Header>
          <Card.Body>
            <p className="text-muted small mb-3">
              Choose how Plum looks to you. Select a theme or let your device decide.
            </p>
            <div className="theme-toggle-group">
              <button
                className={`theme-toggle-option ${preference === 'light' ? 'active' : ''}`}
                onClick={() => setThemePreference('light')}
                aria-label="Light theme"
              >
                <i className="bi bi-sun"></i>
                Light
              </button>
              <button
                className={`theme-toggle-option ${preference === 'dark' ? 'active' : ''}`}
                onClick={() => setThemePreference('dark')}
                aria-label="Dark theme"
              >
                <i className="bi bi-moon-stars"></i>
                Dark
              </button>
              <button
                className={`theme-toggle-option ${preference === 'system' ? 'active' : ''}`}
                onClick={() => setThemePreference('system')}
                aria-label="System theme"
              >
                <i className="bi bi-laptop"></i>
                System
              </button>
            </div>
          </Card.Body>
        </Card>

        {/* Account Actions */}
        <Card className="mb-4 border-0 shadow-sm">
          <Card.Header className="bg-white border-bottom">
            <h6 className="mb-0">
              <i className="bi bi-gear me-2"></i>
              Account
            </h6>
          </Card.Header>
          <Card.Body>
            <Button
              variant="outline-secondary"
              className="w-100 mb-3 d-flex align-items-center justify-content-center"
              onClick={onSignOut}
            >
              <i className="bi bi-box-arrow-right me-2"></i>
              Sign Out
            </Button>
            <div className="d-flex justify-content-center gap-3">
              <Button variant="link" className="text-muted small text-decoration-none p-0" onClick={() => navigate('/terms')}>Terms of Service</Button>
              <Button variant="link" className="text-muted small text-decoration-none p-0" onClick={() => navigate('/privacy')}>Privacy Policy</Button>
            </div>
          </Card.Body>
        </Card>

        {/* Danger Zone */}
        <Card className="border-danger">
          <Card.Header className="bg-danger bg-opacity-10 border-danger">
            <h6 className="mb-0 text-danger">
              <i className="bi bi-exclamation-triangle me-2"></i>
              Danger Zone
            </h6>
          </Card.Header>
          <Card.Body>
            <p className="text-muted small mb-3">
              Permanently delete all your data including projects, documents, and settings.
              This action cannot be undone.
            </p>
            <Button
              variant="outline-danger"
              className="w-100 d-flex align-items-center justify-content-center"
              onClick={handleDeleteClick}
            >
              <i className="bi bi-trash me-2"></i>
              Delete All My Data
            </Button>
          </Card.Body>
        </Card>

        {/* Delete Confirmation Modal */}
        <Modal
          show={showDeleteModal}
          onHide={handleCloseModal}
          centered
          backdrop={isDeleting ? 'static' : true}
        >
          <Modal.Header closeButton={!isDeleting}>
            <Modal.Title className="text-danger">
              <i className="bi bi-exclamation-triangle me-2"></i>
              {deleteStep === 1 ? 'Delete All Data?' : 'Final Confirmation'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {deleteStep === 1 ? (
              <div>
                <Alert variant="danger">
                  <strong>Warning:</strong> This will permanently delete:
                </Alert>
                <ul className="mb-3">
                  <li>All your renovation projects</li>
                  <li>All chat history and documents</li>
                  <li>All uploaded images</li>
                  <li>All work items and specifications</li>
                  <li>All shared project access</li>
                </ul>
                <p className="text-muted mb-0">
                  This action <strong>cannot be undone</strong>. Are you sure you want to continue?
                </p>
              </div>
            ) : (
              <div>
                <Alert variant="danger">
                  <strong>Final Warning:</strong> There is no way to recover your data after deletion.
                </Alert>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Type <strong>delete</strong> to confirm:
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={deleteConfirmation}
                    onChange={(e) => setDeleteConfirmation(e.target.value)}
                    placeholder="Type 'delete' here"
                    disabled={isDeleting}
                    autoFocus
                  />
                </Form.Group>
                {error && (
                  <Alert variant="danger" className="mb-0">
                    {error}
                  </Alert>
                )}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={handleCloseModal}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            {deleteStep === 1 ? (
              <Button variant="danger" onClick={handleFirstConfirm}>
                Yes, Continue
              </Button>
            ) : (
              <Button
                variant="danger"
                onClick={handleFinalDelete}
                disabled={isDeleting || deleteConfirmation.toLowerCase() !== 'delete'}
              >
                {isDeleting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <i className="bi bi-trash me-2"></i>
                    Delete Everything
                  </>
                )}
              </Button>
            )}
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  );
}

export default ProfilePage;
